import { Option } from '@/components/Common/Select';
import { TagContext, TagContexts } from '@/apis/agency/taggings/types';

export type BusinessTagColors = { border: string; color: string; bg: string };

const PURPLE: BusinessTagColors = { border: '#7C3AED', color: '#7C3AED', bg: '#F5F3FF' };
const BLUE: BusinessTagColors = { border: '#2563EB', color: '#2563EB', bg: '#EFF6FF' };
const GREEN: BusinessTagColors = { border: '#059669', color: '#059669', bg: '#ECFDF5' };
const ORANGE: BusinessTagColors = { border: '#D97706', color: '#D97706', bg: '#FFFBEB' };
const RED: BusinessTagColors = { border: '#DC2626', color: '#DC2626', bg: '#FEF2F2' };
const TEAL: BusinessTagColors = { border: '#0D9488', color: '#0D9488', bg: '#F0FDFA' };
const PINK: BusinessTagColors = { border: '#DB2777', color: '#DB2777', bg: '#FDF2F8' };
const DEFAULT_COLORS: BusinessTagColors = { border: '#6B7280', color: '#6B7280', bg: '#F3F4F6' };

const TAG_VALUE_SEPARATOR = '::';

const HIGH_RISK_CHURN_CODES = new Set([
  'no_calls_for_7_consecutive_days',
  'integration_disconnected',
  'below_10x_roi',
  'low_roi',
  'sandbox_enabled'
]);

const CUSTOMER_SUCCESS_COLORS: Record<string, BusinessTagColors> = {
  integration_sponsor: BLUE,
  vip_customer: PINK,
  high_touch: GREEN,
  low_touch: TEAL,
  escalated: RED,
  beta_customer: ORANGE,
  success_story: GREEN
};

export function tagValue(context: string, name: string) {
  return `${context}${TAG_VALUE_SEPARATOR}${name}`;
}

export function parseTagValue(value: string): { context: TagContext; name: string } | null {
  const separatorIndex = value.indexOf(TAG_VALUE_SEPARATOR);
  if (separatorIndex <= 0) return null;

  const context = value.slice(0, separatorIndex) as TagContext;
  const name = value.slice(separatorIndex + TAG_VALUE_SEPARATOR.length).trim();
  if (!name) return null;

  return { context, name };
}

export function titleizeTagName(name: string) {
  return name
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function buildSelectOptionsFromContexts(contexts?: TagContexts): Option[] {
  if (!contexts?.length) return [];

  return contexts.flatMap((group) =>
    group.tags.map((tag) => ({
      value: tagValue(group.context, tag.code),
      label: tag.label,
      category: group.label
    }))
  );
}

export function buildNameSelectOptionsFromContexts(contexts?: TagContexts): Option[] {
  if (!contexts?.length) return [];

  const seen = new Set<string>();

  return contexts.flatMap((group) =>
    group.tags.flatMap((tag) => {
      if (seen.has(tag.code)) return [];
      seen.add(tag.code);
      return [
        {
          value: tag.code,
          label: tag.label,
          category: group.label
        }
      ];
    })
  );
}

export function buildChurnSignalSelectOptions(contexts?: TagContexts): Option[] {
  const group = contexts?.find((entry) => entry.context === 'churn_signal');
  if (!group) return [];

  return group.tags.map((tag) => ({
    value: tag.code,
    label: tag.label,
    category: group.label
  }));
}

export function getTagColors(name: string, context?: TagContext): BusinessTagColors {
  const code = name.toLowerCase().replace(/\s+/g, '_');

  if (context === 'churn_signal') {
    return HIGH_RISK_CHURN_CODES.has(code) ? RED : ORANGE;
  }

  if (context === 'company_size') {
    return code === 'small_business' ? GREEN : PURPLE;
  }

  if (context === 'monthly_call_volume') {
    if (code === 'calls_2k_plus') return PURPLE;
    if (code === 'calls_500_2k') return BLUE;
    return TEAL;
  }

  if (context === 'customer_success') {
    return CUSTOMER_SUCCESS_COLORS[code] ?? DEFAULT_COLORS;
  }

  return DEFAULT_COLORS;
}
